import axios from 'axios';
import apiRequestHeaders from '../apiRequestHeaders';

export interface CreateDac7ReportRequest {
  reportingYear: number;
  notes?: string;
}

export interface Dac7ReportResponse {
  id: string;
  messageRefId: string;
  reportingYear: number;
  reportingPeriod: string;
  platformOperatorName: string;
  platformOperatorTIN: string;
  platformOperatorResCountryCode: string;
  status: string;
  createdAt: string;
  createdBy?: string;
  exportedAt: string;
  exportedBy?: string;
  notes?: string;
}

export interface WinTimeImportResponse {
  id: string;
  dac7ReportId: string;
  importedAt: string;
  importedBy: string;
  fileName: string;
  fileHash?: string;
  totalRows: number;
  processedRows: number;
  errorRows: number;
  requiresReprocessingRows: number;
  requiresRemovalRows: number;
  status: string;
}

export interface ProcessWinTimeDataResponse {
  sellersCreated: number;
  sellersUpdated: number;
  propertyListingsCreated: number;
  propertyListingsUpdated: number;
  rowsProcessed: number;
  errors: string[];
}

export interface ApiResponse<T> {
  isSuccess: boolean;
  message?: string;
  result?: T;
}

// Response for background processing start
export interface ProcessingStartedResponse {
  message: string;
  reportId?: string;
}

// Request for generating DAC7 XML
export interface GenerateDac7XmlRequest {
  docTypeIndic?: string; // OECD1 (new), OECD2 (correction), OECD3 (deletion)
  notes?: string;
}

// Response for DAC7 XML generation
export interface GenerateDac7XmlResponse {
  versionId: string;
  reportId: string;
  versionNumber: number;
  docTypeIndic: string;
  xmlContent: string;
  fileName: string;
  xmlHash: string;
  generatedAt: string;
  generatedBy?: string;
  sellerCount: number;
  propertyListingCount: number;
}

// Request for marking DAC7 report as submitted
export interface MarkAsSubmittedRequest {
  notes?: string;
}

// Create a new DAC7 report for a specific year
export function createDac7Report(
  request: CreateDac7ReportRequest,
): Promise<ApiResponse<Dac7ReportResponse>> {
  const config = {
    headers: {
      ...apiRequestHeaders.csrfHeader(),
      ...apiRequestHeaders.activeSiteHeader(),
    },
  };

  return axios
    .post('/dac7/reports', request, config)
    .then(res => res.data)
    .catch(error => {
      throw error.response?.data || error;
    });
}

// Get all DAC7 reports
export function getDac7Reports(): Promise<ApiResponse<Dac7ReportResponse[]>> {
  const config = {
    headers: {
      ...apiRequestHeaders.csrfHeader(),
      ...apiRequestHeaders.activeSiteHeader(),
    },
  };

  return axios
    .get('/dac7/reports', config)
    .then(res => res.data)
    .catch(error => {
      throw error.response?.data || error;
    });
}

// Get a specific DAC7 report by ID
export function getDac7Report(
  id: string,
): Promise<ApiResponse<Dac7ReportResponse>> {
  const config = {
    headers: {
      ...apiRequestHeaders.csrfHeader(),
      ...apiRequestHeaders.activeSiteHeader(),
    },
  };

  return axios
    .get(`/dac7/reports/${id}`, config)
    .then(res => res.data)
    .catch(error => {
      throw error.response?.data || error;
    });
}

// Import WinTime CSV data for a specific DAC7 report
export function importWinTimeData(
  reportId: string,
  file: File,
): Promise<ApiResponse<WinTimeImportResponse>> {
  const formData = new FormData();
  formData.append('file', file);

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...apiRequestHeaders.csrfHeader(),
      ...apiRequestHeaders.activeSiteHeader(),
    },
  };

  return axios
    .post(`/dac7/reports/${reportId}/wintime-import`, formData, config)
    .then(res => res.data)
    .catch(error => {
      throw error.response?.data || error;
    });
}

// Process imported WinTime data for a specific DAC7 report
// This now starts background processing and returns immediately
// Progress updates are received via SignalR
export function processWinTimeData(
  reportId: string,
): Promise<ProcessingStartedResponse> {
  const config = {
    headers: {
      ...apiRequestHeaders.csrfHeader(),
      ...apiRequestHeaders.activeSiteHeader(),
    },
  };

  return axios
    .post(`/dac7/reports/${reportId}/process`, null, config)
    .then(res => {
      // Handle both string response and object response
      if (typeof res.data === 'string') {
        return { message: res.data, reportId };
      }
      return res.data;
    })
    .catch(error => {
      throw error.response?.data || error;
    });
}

// Get WinTime imports for a specific DAC7 report
export function getWinTimeImports(
  reportId: string,
): Promise<ApiResponse<WinTimeImportResponse[]>> {
  const config = {
    headers: {
      ...apiRequestHeaders.csrfHeader(),
      ...apiRequestHeaders.activeSiteHeader(),
    },
  };

  return axios
    .get(`/dac7/reports/${reportId}/wintime-imports`, config)
    .then(res => res.data)
    .catch(error => {
      throw error.response?.data || error;
    });
}

// Generate DAC7 XML for a specific report
export function generateDac7Xml(
  reportId: string,
  request?: GenerateDac7XmlRequest,
): Promise<ApiResponse<GenerateDac7XmlResponse>> {
  const config = {
    headers: {
      ...apiRequestHeaders.csrfHeader(),
      ...apiRequestHeaders.activeSiteHeader(),
    },
  };

  return axios
    .post(`/dac7/reports/${reportId}/generate-xml`, request || {}, config)
    .then(res => res.data)
    .catch(error => {
      throw error.response?.data || error;
    });
}

// Mark a DAC7 report as submitted to Ilmoitin.fi
export function markDac7ReportAsSubmitted(
  reportId: string,
  request?: MarkAsSubmittedRequest,
): Promise<ApiResponse<Dac7ReportResponse>> {
  const config = {
    headers: {
      ...apiRequestHeaders.csrfHeader(),
      ...apiRequestHeaders.activeSiteHeader(),
    },
  };

  return axios
    .post(`/dac7/reports/${reportId}/mark-as-submitted`, request || {}, config)
    .then(res => res.data)
    .catch(error => {
      throw error.response?.data || error;
    });
}
